"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.translateStrings = translateStrings;
exports.translateLocalizedStrings = translateLocalizedStrings;
exports.canAutoTranslate = canAutoTranslate;
/** Translates an array of strings from a source language to a target language using the mWater translation API.
 * @param strings Array of strings to translate
 * @param sourceLanguage Source language code (e.g. "en")
 * @param targetLanguage Target language code to translate to
 * @returns Promise that resolves to array of translated strings in same order as input
 * @throws Error if translation fails
 */
async function translateStrings(strings, sourceLanguage, targetLanguage) {
    // Call translation API
    const response = await fetch(`https://api.mwater.co/v3/translate?source_language=${sourceLanguage}&target_language=${targetLanguage}`, {
        method: "POST",
        headers: {
            "Content-Type": "application/json"
        },
        body: JSON.stringify({
            phrases: strings
        })
    });
    if (!response.ok) {
        throw new Error("Translation failed");
    }
    const result = await response.json();
    return result.phrases;
}
/** Ensures that all LocalizedString objects have translations for all specified locales.
 * Only translates missing translations, preserving existing ones and base strings.
 * Each string's _base property is used as the source language for translations.
 * @param strings Array of LocalizedString objects to ensure are fully translated
 * @param locales Array of language codes specifying which languages to ensure exist
 * @param baseLanguage Language code of the _base strings (e.g. "en", "fr", etc)
 * @returns Promise that resolves to array of fully translated LocalizedString objects
 */
async function translateLocalizedStrings(strings, locales, baseLanguage) {
    // Create copy of strings to modify
    const translatedStrings = strings.map(str => ({ ...str }));
    // For each locale
    for (const locale of locales) {
        // Skip if it's the base language
        if (locale === baseLanguage) {
            continue;
        }
        // Group strings by their current source language
        // Start with base language strings
        const stringsBySourceLang = new Map();
        translatedStrings.forEach((str, index) => {
            // Skip if translation already exists
            if (str[locale]) {
                return;
            }
            // Add to list to translate from base language
            if (!stringsBySourceLang.has(baseLanguage)) {
                stringsBySourceLang.set(baseLanguage, []);
            }
            stringsBySourceLang.get(baseLanguage).push({ str, index });
        });
        // Translate each group
        for (const [sourceLang, stringsToTranslate] of stringsBySourceLang.entries()) {
            try {
                // Get source strings
                const sourceStrings = stringsToTranslate.map(item => item.str[item.str._base]);
                // Translate all strings at once
                const translations = await translateStrings(sourceStrings, sourceLang, locale);
                // Add translations back to strings
                translations.forEach((translation, i) => {
                    translatedStrings[stringsToTranslate[i].index][locale] = translation;
                });
            }
            catch (error) {
                console.error(`Failed to translate from ${sourceLang} to ${locale}:`, error);
                throw error;
            }
        }
    }
    return translatedStrings;
}
/** Returns true if the given language code can be automatically translated. */
function canAutoTranslate(languageCode) {
    const languageCodes = [
        "en", // English
        "fr", // French
        "es", // Spanish
        "pt", // Portuguese
        "sw", // Swahili
        "id", // Indonesian
        "hi", // Hindi
        "ne", // Nepali
        "rw", // Kinyarwanda
        "sd", // Sindhi
        "tet", // Tetum
        "ur", // Urdu
        "ja", // Japanese
        "de", // German
        "it", // Italian
        "su", // Sundanese
        "ar", // Arabic
        "vi", // Vietnamese
        "mg", // Malagasy
        "tl", // Tagalog/Filipino
        "ht", // Haitian Creole
        "fi", // Finnish
        "bn", // Bangla
        "my", // Burmese
        "km", // Khmer
        "so", // Somali
        "jv", // Javanese
        "ko", // Korean
        "zh", // Chinese
        "ru", // Russian
        "tr", // Turkish
        "nl", // Dutch
        "pl", // Polish
        "hu", // Hungarian
        "cs", // Czech
        "sk" // Slovak
    ];
    return languageCodes.includes(languageCode);
}
